import {
  Alert,
  Checkbox,
  Input,
  Row,
  Space,
  Tag,
  Typography
} from 'antd';
import classNames from 'classnames';
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AnchorTel from 'components/atoms/AnchorTel';
import DefaultButton from 'components/atoms/DefaultButton';
import InternalAdminNote, { InternalAdminMessage } from 'components/atoms/InternalAdminNote';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import FieldsValue from 'components/forms/FieldsValue';
import FieldValue from 'components/forms/FieldValue';
import FormItemCol from 'components/forms/FormItemCol';
import FormItemInputPhoneNumber from 'components/forms/FormItemInputPhoneNumber';
import { EM_DASH } from 'constants/index';
import { INTERNAL_ADMIN_ROLE_TO_LABEL, InternalAdminRole } from 'constants/internal-admin-roles';
import useTranslation from 'hooks/useTranslation';
import { useEnterpriseId } from 'store/modules/enterprise/hooks';
import clearErrors from 'store/modules/error/actions';
import { makeSelectFirstErrorMessage } from 'store/modules/error/selectors';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import MyUserActionType from 'store/modules/myUser/constants';
import { useIsEnterpriseAdmin, useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { makeSelectIsMyUser } from 'store/modules/myUser/selectors';
import UserActionType from 'store/modules/users/constants';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import Membership, { UserRoleLabels } from 'types/models/membership';
import { UserId } from 'types/models/user';

import FormItemEmail from '../FormItemEmail';
import FormItemRole from '../FormItemRole';
import { UserRemoveButton } from './components/UserRemoveButton';

import './styles.less';

const actions = [
  UserActionType.ADD_USER_REQUEST,
  UserActionType.EDIT_USER_REQUEST,
  MyUserActionType.EDIT_MY_USER_REQUEST
];

const selectErrorMessage = makeSelectFirstErrorMessage(actions);
const selectIsLoading = makeSelectIsLoading(actions);

interface Props {
  namePrefix?: string;
  userId?: UserId;
  readOnly: boolean;
  showEmailField?: boolean;
  showRoleField?: boolean;
  showSaveButton?: boolean;
  showUsernameField?: boolean;
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  afterUserRemoved?: () => void;
  required?: boolean;
}

function UserFormItems({
  userId,
  readOnly,
  showSaveButton: showSaveButtonProp,
  showEmailField = true,
  showRoleField = true,
  showUsernameField = true,
  isFullSpanOnMobileHalfOnDesktop,
  afterUserRemoved,
  required = true
}: Props): JSX.Element | null {
  const dispatch = useDispatch();
  const errorMessage = useSelector(selectErrorMessage);
  const isLoading = useSelector(selectIsLoading);

  const { t } = useTranslation();

  const isEnterpriseAdmin = useIsEnterpriseAdmin();
  const hasInternalPermissionToRemoveMembership = useMyUserHasInternalPermission({
    resource: ProtectedResource.USER_MEMBERSHIP,
    action: ResourceAction.DELETE
  });
  const hasRemoveMembershipPermission = isEnterpriseAdmin || hasInternalPermissionToRemoveMembership;

  const currentEnterpriseId = useEnterpriseId();
  const isCurrentUser = useSelector(makeSelectIsMyUser(userId || 0));

  const mobileNumberLabel = t('MOBILE_NUMBER');

  useEffect(() => {
    dispatch(clearErrors(actions));
  }, [dispatch]);

  const showSaveButton = showSaveButtonProp ?? !readOnly;
  /**
   * Who can edit roles?
   *
   * |               |admin|non-admin|
   * |---------------|-----|---------|
   * |can edit self  |  ❌  |   ❌    |
   * |can edit others|  ✅  |   ❌    |
   *
   * This matrix (better to view in source code) applies no matter in Team
   * Member editing page or Profile Settings.
   */
  const allowEditRole = !readOnly && !isCurrentUser;
  /**
   * Who can remove membership?
   *
   * |                 |admin|non-admin|
   * |-----------------|-----|---------|
   * |can remove self  | ❌  |   ❌    |
   * |can remove others| ✅  |   ❌    |
   *
   * This matrix applies no matter in Team Member editing page or Profile Settings.
   */
  const showRemoveButton = !!userId && hasRemoveMembershipPermission && !isCurrentUser;

  return (
    <>
      <Row gutter={24}>
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          <DefaultFormItem
            name="firstName"
            label="First Name"
            rules={[
              {
                required,
                message: 'Please enter a first name'
              },
              {
                pattern: /^[A-Za-z ,.'-]+$/,
                message: 'First Name should not contain numbers'
              }
            ]}
            readOnly={readOnly}
          >
            <Input placeholder="First Name" />
          </DefaultFormItem>
        </FormItemCol>

        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          <DefaultFormItem
            name="lastName"
            label="Last Name"
            rules={[
              {
                required,
                message: 'Please enter a last name'
              },
              {
                pattern: /^[A-Za-z ,.'-]+$/,
                message: 'Last Name should not contain numbers'
              }
            ]}
            readOnly={readOnly}
          >
            <Input placeholder="Last Name" />
          </DefaultFormItem>
        </FormItemCol>

        {showUsernameField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="username"
              label="Username"
              rules={[
                {
                  required,
                  message: 'Please enter a username'
                },
                {
                  pattern: new RegExp(
                    '^[a-zA-Z0-9](_(?!(\\.|_))|\\.(?!(_|\\.))|[a-zA-Z0-9#]){4,28}[a-zA-Z0-9]$'
                  ),
                  message: 'Please enter a valid username'
                }
              ]}
              readOnly={readOnly}
            >
              <Input placeholder="Username" />
            </DefaultFormItem>
          </FormItemCol>
        )}
        {showEmailField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <FormItemEmail readOnly={readOnly} required={required} />
          </FormItemCol>
        )}
        {/* Mobile Number */}
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          {readOnly ? (
            <FieldValue name="fullName">
              {(userFullName = 'This user') => (
                <DefaultFormItem
                  readOnly
                  name="mobileNumberFormatted"
                  label={mobileNumberLabel}
                  formatReadOnlyValue={(value) => {
                    if (!value) {
                      return (
                        <Typography.Text type="secondary">
                          {userFullName} hasn&apos;t provided a {mobileNumberLabel.toLowerCase()} yet.
                        </Typography.Text>
                      );
                    }
                    return <AnchorTel phoneNumber={value} />;
                  }}
                />
              )}
            </FieldValue>
          ) : (
            <FieldValue name="enableSmsNotifications">
              {(smsNotificationsEnabled = false) => (
                <FormItemInputPhoneNumber
                  name="mobileNumber"
                  label={mobileNumberLabel}
                  rules={[
                    {
                      required: !!smsNotificationsEnabled && required,
                      message: `Please enter a ${mobileNumberLabel.toLowerCase()}`
                    }
                  ]}
                  prefixName="mobileNumberCountryCode"
                />
              )}
            </FieldValue>
          )}
        </FormItemCol>
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          {readOnly ? (
            <FieldValue name="fullName">
              {(userFullName = 'This user') => (
                <DefaultFormItem
                  readOnly
                  name="landlineNumberFormatted"
                  label="Landline Number"
                  formatReadOnlyValue={(value) => {
                    if (!value) {
                      return (
                        <Typography.Text type="secondary">
                          {userFullName} hasn&apos;t provided a landline number yet.
                        </Typography.Text>
                      );
                    }
                    return <AnchorTel phoneNumber={value} />;
                  }}
                />
              )}
            </FieldValue>
          ) : (
            <FormItemInputPhoneNumber
              name="landlineNumber"
              label="Landline Number"
              rules={[
                {
                  required: false,
                  message: 'Please enter a landline number'
                }
              ]}
              prefixName="landlineNumberCountryCode"
            />
          )}
        </FormItemCol>
        {showRoleField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            {allowEditRole ? (
              <FormItemRole rules={[{ required, message: 'Please select a role' }]} />
            ) : (
              <DefaultFormItem label="Role" required>
                <FieldsValue names={['role', 'adminRole']}>
                  {({ role, adminRole }) => {
                    // non-admin
                    if (!adminRole || adminRole === InternalAdminRole._NONE) {
                      const roleLabel = UserRoleLabels[role] || role;
                      return roleLabel ? <Tag>{roleLabel.toUpperCase()}</Tag> : <>{EM_DASH}</>;
                    }
                    // internal admin or both roles
                    return (
                      <>
                        {[role, adminRole].filter(Boolean).map(r => {
                          const roleLabel = INTERNAL_ADMIN_ROLE_TO_LABEL[r] || UserRoleLabels[r] || r;
                          return <Tag key={r}>{roleLabel.toUpperCase()}</Tag>;
                        })}
                      </>
                    );
                  }}
                </FieldsValue>
              </DefaultFormItem>
            )}
          </FormItemCol>
        )}
      </Row>
      {!readOnly && (
        <FieldValue name="memberships">
          {(memberships: Membership[] = []) => {
            const isCreatingNewUser = !userId;
            const isEditingEnterpriseMember = memberships.some(m => m.enterpriseId === currentEnterpriseId);
            const allowSetNotificationPreferences = isCreatingNewUser || isEditingEnterpriseMember;
            return (
              <Row>
                <FormItemCol
                  isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
                >
                  <Space direction="vertical">
                    <Typography.Text strong>Notifications Preference</Typography.Text>
                    <InternalAdminNote message={InternalAdminMessage.SUBSCRIBE_TO_ALERTS} />
                    <Typography.Text>Send alert notifications via</Typography.Text>
                    <DefaultFormItem
                      name="enableEmailNotifications"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox disabled={!allowSetNotificationPreferences}>Email</Checkbox>
                    </DefaultFormItem>
                    <DefaultFormItem
                      name="enableSmsNotifications"
                      valuePropName="checked"
                    >
                      <Checkbox disabled={!allowSetNotificationPreferences}>{t('SMS')}</Checkbox>
                    </DefaultFormItem>
                  </Space>
                </FormItemCol>
              </Row>
            );
          }}
        </FieldValue>
      )}
      {errorMessage && (
        <DefaultFormItem>
          <Alert message={errorMessage} type="error" />
        </DefaultFormItem>
      )}
      <div
        className={classNames(
          'UserFormItems-footer',
          { 'UserFormItems-footer-one-button': !showRemoveButton },
          { 'UserFormItems-footer-two-buttons': showRemoveButton }
        )}
      >
        {showRemoveButton && (
          <FieldValue name="fullName">
            {(userFullName = 'this user') => (
              <UserRemoveButton
                userId={userId}
                userFullName={userFullName}
                isFormSaving={isLoading}
                afterUserRemoved={afterUserRemoved}
              />
            )}
          </FieldValue>
        )}
        {showSaveButton && (
          <DefaultButton
            type="primary"
            loading={isLoading}
            htmlType="submit"
          >
            SAVE
          </DefaultButton>
        )}
      </div>
    </>
  );
}

export default memo(UserFormItems);
